// Common constants and utilities for column configuration
import { tokens } from '../../Common/designTokens'; // added
import { withUiFilterKeyAliases } from '../../Utils/filterKeyAliases';

// Type constants
export const BOND_TYPES = {
  Treasury: "Gov",
  Corporates: "Corp",
  Municipals: "Muni"
};

// Styling constants
export const COMMON_STYLES = {
  button: {
    justifyContent: "flex-start",
    maxWidth: "100%",
    minWidth: "auto",
    padding: "2px 4px"
  },
  typography: {
    textTransform: "capitalize",
    fontWeight: "bold",
    textDecoration: "underline",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "block",
    color: tokens.colors.fontColour
  }
};

// Field mapping constants
export const STRING_FIELDS = ['side', 'state', 'paymentFrequency', 'sector', 'incomeSource', 'issuer', 'underlyingRating', 'securityCharge'];

export const NUMERIC_FIELD_MAPPING = [
  { src: 'couponMin', dest: 'minCoupon' },
  { src: 'couponMax', dest: 'maxCoupon' },
  { src: 'yieldMin', dest: 'minYtm' },
  { src: 'yieldMax', dest: 'maxYtm' },
  { src: 'priceMin', dest: 'minPrice' },
  { src: 'priceMax', dest: 'maxPrice' }
];

export const BOOLEAN_FIELDS = ['insured', 'altMinTax', 'taxable', 'zeroCoupon'];

/**
 * Formats a date string to YYYY-MM-DD without time component
 * @param {string} dateStr - The date string to format
 * @returns {string|null} - The formatted date or original string if invalid
 */
export const formatDateOnly = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? dateStr : date.toISOString().split('T')[0];
  } catch (e) {
    console.error("Error formatting date:", e);
    return dateStr;
  }
};

/**
 * Preprocesses the characteristics data into filter format
 * @param {object} data - The raw characteristics data
 * @returns {object} - Processed filter data
 */
export const preprocessFilters = (data) => {
  if (!data || Object.keys(data).length === 0) return {};

  const filterInput = {};

  // Handle bond type conversion
  let type = data.type;
  if (!type && data.bondType) {
    type = BOND_TYPES[data.bondType] || data.bondType;
  }

  if (type) filterInput.type = type;

  // Process string fields
  STRING_FIELDS.forEach(field => {
    if (data[field]) filterInput[field] = data[field];
  });

  // Moody's rating handling for Corp and Muni types
  if ((type === 'Corp' || type === 'Muni') && data.minimumRatingMoody) {
    filterInput.minRatingMoody = data.minimumRatingMoody;
    filterInput.maxRatingMoody = 'Aaa';
  }

  // Handle minimumRatingMoody field directly if it exists (for display purposes)
  if (data.minimumRatingMoody) {
    filterInput.minimumRatingMoody = data.minimumRatingMoody;
  }

  // Process date fields — either spelling may be stored (the form writes maturityMin/Max,
  // the ladder roll-over job writes minMaturity/maxMaturity) and the value may be a "5Y"
  // tenor or an absolute date. formatDateOnly returns non-dates untouched, so the chip
  // renders both forms.
  const maturityMinValue = data.maturityMin ?? data.minMaturity;
  const maturityMaxValue = data.maturityMax ?? data.maxMaturity;
  if (maturityMinValue) filterInput.minMaturity = formatDateOnly(maturityMinValue);
  if (maturityMaxValue) filterInput.maxMaturity = formatDateOnly(maturityMaxValue);
  if (data.nextCallDate) filterInput.nextCallDate = formatDateOnly(data.nextCallDate);

  // Process numeric fields — same either-spelling tolerance (couponMin vs minCoupon, etc.)
  NUMERIC_FIELD_MAPPING.forEach(({ src, dest }) => {
    const value =
      data[src] !== undefined && data[src] !== '' ? data[src] : data[dest];
    if (value !== undefined && value !== '' && !isNaN(value)) {
      filterInput[dest] = Number(value);
    }
  });

  // Handle duration
  if (data.duration) {
   try{
      const durationValue = data.duration.replace(/[<>=]/g, '');
      if (data.duration.startsWith('>=')) {
        filterInput.minDuration = Number(durationValue);
      } else if (data.duration.startsWith('<=')) {
        filterInput.maxDuration = Number(durationValue);
      } else {
        filterInput.duration = data.duration;
      }
   }catch (error) {
      console.error("Error processing duration:", error);
    }
  }

  // Process boolean fields
  BOOLEAN_FIELDS.forEach(field => {
    if (data[field] !== undefined && data[field] !== '') {
      filterInput[field] = data[field] === 'Yes' || data[field] === true ? 1 : 0;
    }
  });

  // Special handling for callable
  if (data.callable) {
    filterInput.callable = data.callable === 'Yes' ? 'Yes' : 'No';
  } else if (data.call) {
    filterInput.callable = data.call === 'Yes' ? 'Yes' : 'No';
  }

  // Rating handling for Corp and Muni types - handle minimum rating
  if ((type === 'Corp' || type === 'Muni') && data.minimumRating) {
    filterInput.minRatingSP = data.minimumRating;
    filterInput.maxRatingSP = 'AAA';
  }

  // Handle minimumRating field directly if it exists (for display purposes)
  if (data.minimumRating) {
    filterInput.minimumRating = data.minimumRating;
  }

  return filterInput;
};

/**
 * Parses characteristics data from string or object
 * @param {string|object} characteristics - The characteristics data to parse
 * @returns {object} - Parsed characteristics object
 */
export const parseCharacteristics = (characteristics) => {
  if (!characteristics) return {};

  try {
    const parsed = typeof characteristics === 'object' ? characteristics : JSON.parse(characteristics);
    // Stored under the backend spelling (minCoupon, ...), consumed under the UI spelling
    // (couponMin, ...) — expose both so either side can read it
    return withUiFilterKeyAliases(parsed);
  } catch (error) {
    console.error("Error parsing characteristics:", error);
    return {};
  }
};

/**
 * Build the column configuration for standard orders
 * @param {string} userRole - The user role (PM or Trader)
 * @param {string} view - View parameter
 * @param {string} tab - Tab parameter
 * @returns {Array} - Array of column configurations
 */
export const buildOrderColumns = (userRole, view, tab) => {
  const shouldHideColumns = (view === '0' && tab === '0') || !view || !tab;
  const isStagingBlotter = view === '0' && tab === '0';

  // Fixed leading columns: View Inventory (when present) → Actions → Protocol → Account
  const fixedColumns = [
    // viewInventory is added separately in getOrderColumns
    ...(!isStagingBlotter ? [{ key: "actions", name: "Actions" }] : []),
    ...(!shouldHideColumns ? [{ key: "protocol", name: "Protocol" }] : []),
    { key: "account", name: "Account" },
  ];

  // NEW: Enhanced RFQ detail columns (only when not staging blotter)
  const enhancedRfqColumns = !isStagingBlotter ? [
    { key: "rfqInquiryType", name: "RFQ Inquiry Type" },
    // { key: "settlementDate", name: "Settlement Date" },
    { key: "priceType", name: "Price Type" },
    { key: "stipulationType", name: "Stipulation Type" },
    { key: "allowPartialExecution", name: "Allow Partial Exec" },
    { key: "quoteSubmissionDeadline", name: "Quote Deadline (min)" },
    { key: "ttl", name: "Quote Firm Deadline (min)" },
    // MarketAxess-specific fields grouped at the end
    { key: "mktxTargetLevel", name: "Target Level" },
    { key: "mktxPricingProcess", name: "Pricing Process" },
    { key: "mktxDealerListMode", name: "Dealer List Mode" },
    // NEW
    { key: "effectiveTime", name: "Effective Time" },
    { key: "strategyType", name: "Strategy Type" },

  ] : [];

  if (isStagingBlotter) {
    return [
      ...fixedColumns,
      { key: "side", name: "Side" },
      { key: "bondLadderRungId", name: "Rung ID", nonResizable: true, nonDraggable: true },
      { key: "cusip", name: "Identifier" },
      { key: "quantity", name: "Qty" },
      { key: "price", name: "Price" },
      { key: "userName", name: "User Name" },
      { key: "description", name: "Description" },
      { key: "coupon", name: "Coupon" },
      { key: "maturity", name: "Maturity" },
      { key: "venue", name: "Dealer" },
      { key: "status", name: "Status" },
      { key: "comments", name: "Comment" },
      { key: "executedQuantity", name: "Executed Quantity" },
      { key: "executedAveragePrice", name: "Executed Average Price" },
      { key: "dateTime", name: "Order Date" },
    ];
  }

  return [
    ...fixedColumns,
    { key: "side", name: "Side" },
    { key: "cusip", name: "Identifier" },
    { key: "description", name: "Description" },
    { key: "quantity", name: "Qty" },
    { key: "price", name: "Price" },
    { key: "venue", name: "Dealer" },
    { key: "executedQuantity", name: "Executed Quantity" },
    { key: "executedAveragePrice", name: "Executed Average Price" },
        // List trading: which list this RFQ was raised in, rendered LST-000001 by formatListId -
    // see the renderer in columnConfig.getOrderColumns. Blank ("---") on a single-bond RFQ,
    // which belongs to no list.
    //
    // Placed with the other identity columns (Protocol, Account) rather than out by the RFQ
    // detail fields, so it is readable without scrolling sideways - a trader scanning the
    // blotter needs to see at a glance which rows were raised together. This is only the
    // DEFAULT position; the grid is draggable and a user's own column order wins.
    //
    // Orders blotter only. The staging blotter above does not carry it because a list is sent
    // as it is raised and never sits in staging.
    { key: "listId", name: "List ID" },
    // The trader's name for the list, beside its number; "---" when none was given.
    { key: "listName", name: "List Name" },
    { key: "status", name: "Status" },
    { key: "tradeType", name: "Trade Type" },
    { key: "counterPartyName", name: "Counterparty" },
    ...(shouldHideColumns ? [] : [{ key: "orderExpiryDtTime", name: "Expiry" }]),
    { key: "rfqDisplayStatus", name: "RFQ Status" },
    { key: "rejectReason", name: "Reject Reason" },
    ...enhancedRfqColumns,
    { key: "userName", name: "User Name" },
    { key: "coupon", name: "Coupon" },
    { key: "maturity", name: "Maturity" },
    { key: "comments", name: "Comment" },
    { key: "dateTime", name: "Order Date" },
  ];
};

/**
 * Build the base column configuration for bond characteristic orders
 * @param {string} userRole - The user role (PM or Trader)
 * @returns {Array} - Array of column configurations
 */
export const buildBondCharColumns = (userRole) => [
  { key: "side", name: "Side" },
  { key: "type", name: "Type" },
  // ...(userRole === "Trader" ? [{ key: "pm", name: "PM" }] : []),
  { key: "userName", name: "User Name" },
  { key: "account", name: "Account" },
  { key: "qty", name: "Qty" },
  { key: "status", name: "Status" },
  // Bond ladder roll-over: populated only when the characteristic belongs to a rung
  { key: "ladderName", name: "Ladder Name" },
  { key: "reason", name: "Reason" },
  { key: "comments", name: "Comment" },
  { key: "additional_comments", name: "Remarks" },
];

/**
 * Build the base column configuration for bond ladder orders
 * @param {string} userRole - The user role (PM or Trader)
 * @returns {Array} - Array of column configurations
 */
export const buildBondLadderColumns = (userRole) => [
  { key: "side", name: "Side" },
  { key: "type", name: "Type" },
  // ...(userRole === "Trader" ? [{ key: "pm", name: "PM" }] : []),
  { key: "userName", name: "User Name" },
  { key: "account", name: "Account" },
  { key: "qty", name: "Qty" },
  { key: "nameOfTheLadder", name: "Ladder Name" },
  { key: "numberOfRungs", name: "Rungs" },
  { key: "amountPerRung", name: "Amount Per Rung" },
  { key: "totalMaturity", name: "Total Maturity (Months)" },
  { key: "status", name: "Status" },
  { key: "comments", name: "Comment" },
  { key: "additional_comments", name: "Remarks" },

];
